/**
 * Cotizar: el formulario de la opcion elegida y la cotizacion resultante.
 *
 * ---------------------------------------------------------------------------
 * ESTA PANTALLA NO CALCULA NADA
 *
 * No suma lineas, no aplica markup, no deriva un margen, no decide si un piso
 * se cumple. Todo eso lo hizo `packages/pricing` y lo devolvio la API, y este
 * archivo lo pinta. Lo unico que hace con un monto es formatearlo.
 *
 * El motivo no es de arquitectura. Un margen recalculado aqui daria un numero
 * ligeramente distinto --otra libreria, otro redondeo, otro orden de suma-- y
 * ese numero apareceria junto al que se congelo, contradiciendolo. Los montos
 * de una cotizacion son los que se enviaron al cliente y la empresa esta
 * obligada a honrarlos [Invariante 5]; no puede haber una segunda opinion.
 *
 * VISIBILIDAD. `costo` y `margen` vienen o no vienen segun `usuario.ve_costo`,
 * y quien lo decide es `fn_opcion_linea_agente` en la base. Esta pantalla no
 * pregunta si el usuario ve costos para decidir que oculta: pinta las columnas
 * que llegaron. Si `costo` no esta en la respuesta, no hay nada que ocultar
 * porque nunca salio del servidor. El aviso de `ve_costo` es informativo, para
 * que el agente sepa por que no ve una columna, no un interruptor.
 * ---------------------------------------------------------------------------
 */

import type {
  CargoNoValorado,
  CriterioResuelto,
  LineaCotizacion,
  RespuestaCotizacion,
  ResultadoTarifa,
  Tercero,
} from '../api';
import { dato, el, literal } from '../dom';
import type { Traductor } from '../i18n';
import {
  formatearFecha,
  formatearMoneda,
  formatearPorcentaje,
  formatearRango,
  presentarCarrier,
  type Idioma,
} from '../formato';
import { MAX_UNIDADES } from '../peticiones';
import { mapaRuta, mapaSinCoordenadas } from './mapa';
import { pesoTarifable } from './aereo';
import { resumirCarga } from './carga';

/**
 * La declaracion de carga, cuando dice algo.
 *
 * PELIGROSA se marca en rojo y las demas en neutro. No es decoracion: una
 * cotizacion de mercancia peligrosa que se tramita como general se rechaza en
 * puerta, y el documento acaba delante de un cliente y de un operativo que
 * tienen que verlo sin buscarlo.
 */
function avisoDeCarga(
  carga: Record<string, unknown> | undefined,
  t: Traductor,
): HTMLElement | null {
  const resumen = resumirCarga(carga, t);
  if (resumen === null) return null;

  const peligrosa = carga?.naturaleza === 'PELIGROSA';
  return el('div', { clase: `aviso ${peligrosa ? 'aviso-critico' : 'aviso-neutro'}` }, [
    el('strong', { texto: t('carga.enCotizacion') }),
    el('p', { texto: resumen }),
  ]);
}

export interface EstadoFormularioCotizacion {
  readonly unidades: number | null;
  readonly clienteId: string | null;
}

export interface OpcionesFormulario {
  readonly t: Traductor;
  readonly idioma: Idioma;
  readonly tarifa: ResultadoTarifa;
  readonly clientes: readonly Tercero[];
  readonly valores: EstadoFormularioCotizacion;
  readonly creando: boolean;
  readonly faltan: readonly string[];
  readonly onCambio: (v: EstadoFormularioCotizacion) => void;
  readonly onCrear: () => void;
  readonly onCancelar: () => void;
}

export function pintarFormularioCotizacion(o: OpcionesFormulario): HTMLElement {
  const { t, idioma, tarifa } = o;
  const carrier = presentarCarrier(tarifa.carrier);

  // LA COTIZACION AEREA HEREDA LA ANATOMIA DE LA BUSQUEDA: por kilo, no por
  // contenedor. Lo decide el bloque `aereo` de la tarifa elegida, que es el
  // mismo dato que decidio la tabla de la que salio.
  //
  // Lo que cambia no es una etiqueta: es que en aereo NO HAY CONTENEDORES QUE
  // CONTAR. Un campo "Contenedores" en la cotizacion de un vuelo pide un numero
  // que no multiplica a nada --el multiplicador son los kilos-- y que la API
  // rechaza; dejarlo puesto invita a escribir un 2 y a creer que se estan
  // cotizando dos de algo.
  const aereo = tarifa.aereo;

  const unidades = el('input', {
    clase: 'control',
    attrs: {
      id: 'campo-unidades',
      type: 'number',
      min: '1',
      max: String(MAX_UNIDADES),
      step: '1',
      value: o.valores.unidades === null ? '' : String(o.valores.unidades),
    },
  });
  unidades.addEventListener('input', () => {
    // `valueAsNumber` da NaN con el campo vacio. Se normaliza a null: null es
    // "todavia no lo escribio" y NaN se colaria como unidades invalidas.
    const n = unidades.valueAsNumber;
    o.onCambio({ ...o.valores, unidades: Number.isFinite(n) ? n : null });
  });

  const cliente = el('select', {
    clase: 'control',
    attrs: { id: 'campo-cliente' },
  });
  cliente.appendChild(
    el('option', { texto: t('cotizador.eligeCliente'), attrs: { value: '' } }),
  );
  for (const c of o.clientes) {
    cliente.appendChild(
      el('option', {
        texto: c.pais === null ? c.nombre : `${c.nombre} (${c.pais})`,
        attrs: { value: c.id },
      }),
    );
  }
  cliente.value = o.valores.clienteId ?? '';
  cliente.addEventListener('change', () => {
    o.onCambio({ ...o.valores, clienteId: cliente.value === '' ? null : cliente.value });
  });

  if (o.faltan.includes('unidades')) unidades.classList.add('falta');
  if (o.faltan.includes('cliente_id')) cliente.classList.add('falta');

  return el('section', { clase: 'panel panel-cotizar' }, [
    el('h2', { texto: t('cotizador.titulo') }),

    // La tarifa elegida, resumida: el agente tiene que poder confirmar que esta
    // cotizando la que creia. Carrier con su estado, igual que en la lista.
    el('div', { clase: 'resumen-tarifa' }, [
      dato(
        t('cotizador.tarifaElegida'),
        carrier.clave === null ? (carrier.nombre ?? '') : t(carrier.clave),
      ),
      // El total del catalogo, si este usuario lo recibio. Sin `ve_costo` no
      // llego, y el resumen lo dice en vez de dejar un hueco: lo que se esta
      // eligiendo se identifica por carrier y equipo, no por el precio.
      dato(
        t('resultado.total'),
        tarifa.total === undefined
          ? t('resultado.sinCosto')
          : formatearRango(
              tarifa.total.costo_min,
              tarifa.total.costo_max,
              tarifa.total.moneda,
              idioma,
            ),
      ),
      // En aereo el equipo no existe y en su sitio va lo que SI se vende: los
      // kilos que se cobran y el escalon que los tarifa. Dejar "Equipo —" seria
      // un hueco donde no falta nada [H59].
      aereo === undefined
        ? dato(t('busqueda.equipo'), tarifa.dias_libres.equipo)
        : dato(
            t('aereo.pesoTarifable'),
            `${aereo.peso.tarifable_kg} ${t('aereo.kg')} (${aereo.escalon.etiqueta})`,
          ),
      dato(t('grupo.advertencia'), t(`alcance.${tarifa.alcance}`)),
    ]),

    // La misma explicacion que en la lista, entera: es el ultimo sitio donde el
    // agente puede darse cuenta de que se cobran 500 kilos de 120 antes de
    // emitir un documento que hay que honrar.
    aereo === undefined
      ? null
      : el('div', { clase: 'cotizar-aereo' }, [pesoTarifable(aereo, t)]),

    el('div', { clase: 'formulario-cotizar' }, [
      // Solo fuera del aereo. Ver arriba.
      aereo !== undefined
        ? null
        : el('div', { clase: 'campo' }, [
            el('label', {
              clase: 'etiqueta',
              texto: t('cotizador.unidades'),
              attrs: { for: 'campo-unidades' },
            }),
            unidades,
          ]),
      el('div', { clase: 'campo campo-ancho' }, [
        el('label', {
          clase: 'etiqueta',
          texto: t('cotizador.cliente'),
          attrs: { for: 'campo-cliente' },
        }),
        cliente,
      ]),
      el('div', { clase: 'campo campo-accion' }, [
        el('button', {
          clase: 'boton boton-plano',
          texto: t('cotizador.cancelar'),
          attrs: { type: 'button' },
          onClick: o.onCancelar,
        }),
        el('button', {
          clase: 'boton boton-primario',
          texto: o.creando ? t('cotizador.creando') : t('cotizador.crear'),
          attrs: o.creando
            ? { type: 'button', disabled: 'disabled' }
            : { type: 'button' },
          onClick: o.onCrear,
        }),
      ]),
    ]),

    // [CLAUDE.md, Idioma] El idioma del documento es el que tiene puesto el
    // agente ahora y queda congelado. Se dice antes de crearla, no despues.
    el('p', {
      clase: 'tenue',
      texto: `${t('cotizador.idiomaDocumento')}: ${idioma}`,
    }),
  ]);
}

// ---------------------------------------------------------------------------
// La cotizacion creada
// ---------------------------------------------------------------------------

export function pintarCotizacion(
  r: RespuestaCotizacion,
  t: Traductor,
  idioma: Idioma,
  /**
   * La ruta que se cotizo, para el mapa. La respuesta de la API no la trae:
   * identifica la opcion por `rate_record_id`, no por sus puertos.
   *
   * `null` cuando la busqueda ya no esta en pantalla. Entonces no se pinta
   * mapa, y ya. La cotizacion es un documento con folio y se enseña entera: que
   * falte el adorno no puede esconderla.
   */
  criterio: CriterioResuelto | null,
  onOtra: () => void,
): HTMLElement {
  const moneda = r.cotizacion.moneda_venta;

  return el('section', { clase: 'panel panel-cotizacion' }, [
    el('header', { clase: 'cotizacion-cabecera' }, [
      el('h2', { texto: `${t('cotizador.folio')} ${r.cotizacion.folio}` }),
      el('span', {
        clase: 'chip chip-estado',
        texto: t(`estadoCotizacion.${r.cotizacion.estado}`),
      }),
      el('span', {
        clase: 'tenue',
        texto: `${t('cotizador.validaHasta')} ${formatearFecha(r.cotizacion.valida_hasta, idioma)}`,
      }),
      el('span', {
        clase: 'tenue',
        // El idioma congelado del documento, que puede no ser el de la pantalla
        // si alguien lo cambia despues.
        texto: `${t('cotizador.idiomaDocumento')}: ${r.cotizacion.idioma}`,
      }),
    ]),

    // Que es lo que viaja, arriba y con el resto de lo que define el documento.
    // Solo aparece cuando hay algo que decir: "carga general" en cada
    // cotizacion se deja de leer, y con ella se deja de leer "PELIGROSA".
    avisoDeCarga(r.cotizacion.carga as Record<string, unknown> | undefined, t),

    // [Invariante 11] El aviso de aprobacion, arriba y sin abreviar. Con el
    // piso al 4% ya no salta en toda cotizacion: cuando aparece, significa algo.
    r.requiere_aprobacion
      ? el('div', { clase: 'aviso aviso-aprobacion' }, [
          el('strong', { texto: t('cotizador.requiereAprobacion') }),
          r.motivo_aprobacion === null
            ? null
            : el('p', {
                texto: `${t('cotizador.motivoAprobacion')}: ${r.motivo_aprobacion}`,
              }),
          r.piso_aplicado === null
            ? null
            : el('p', {
                clase: 'tenue',
                // El texto de `piso_margen.descripcion`. Es lo unico que dice
                // contra que numero se midio la cotizacion.
                texto: `${t('cotizador.pisoAplicado')}: ${r.piso_aplicado}`,
              }),
        ])
      : null,

    r.ve_costo
      ? null
      : el('p', { clase: 'tenue nota-visibilidad', texto: t('cotizador.sinCostos') }),

    // Documento: ya se eligio la tarifa y esto es de una sola ruta. Va a ancho
    // completo antes de las lineas, que es el orden en que se lee una
    // cotizacion --primero que se compra y luego cuanto-- y porque esto acaba
    // delante de un cliente. Si falta la coordenada
    // de un puerto se dice CUAL falta, aqui si: en la cotizacion el mapa es
    // parte de lo que se esperaba ver. Ver vista/mapa.ts.
    criterio === null
      ? null
      : (mapaRuta(criterio, 'documento', t)?.elemento ?? mapaSinCoordenadas(criterio, t)),

    pintarLineas(r.lineas, t, idioma, moneda, r.ve_costo),

    el('div', { clase: 'totales' }, [
      dato(
        t('cotizador.venta'),
        formatearMoneda(r.totales.venta, moneda, idioma),
        'total-fila total-venta',
      ),
      // Ausentes cuando el usuario no ve costos: no llegaron del servidor.
      r.totales.costo === undefined
        ? null
        : dato(
            t('cotizador.costo'),
            formatearMoneda(r.totales.costo, moneda, idioma),
            'total-fila',
          ),
      r.totales.margen === undefined
        ? null
        : dato(
            t('cotizador.margen'),
            formatearMoneda(r.totales.margen, moneda, idioma),
            'total-fila',
          ),
      r.totales.margen_pct === undefined
        ? null
        : dato(
            t('cotizador.margenPct'),
            formatearPorcentaje(r.totales.margen_pct, idioma),
            'total-fila',
          ),
    ]),

    // [Invariante 12][H25] No se evaluaron: el agente lee la condicion y decide.
    pintarNoValorados(
      t('cotizador.condicionesARevisar'),
      r.condiciones_a_revisar,
      t,
      'aviso-condiciones',
    ),
    pintarNoValorados(t('cotizador.cargosNoValorados'), r.cargos_no_valorados, t, ''),

    el('footer', { clase: 'cotizacion-acciones' }, [
      el('button', {
        clase: 'boton boton-plano',
        texto: t('cotizador.otra'),
        attrs: { type: 'button' },
        onClick: onOtra,
      }),
    ]),
  ]);
}

/**
 * La tabla de lineas. Exportada porque la zona de Cotizaciones repinta lo mismo
 * al reabrir un folio: son las mismas columnas leidas de la misma funcion de la
 * base, y dos tablas iguales se separan en cuanto se toca una.
 */
export function pintarLineas(
  lineas: readonly LineaCotizacion[],
  t: Traductor,
  idioma: Idioma,
  moneda: string,
  veCosto: boolean,
): HTMLElement {
  const cabecera = el('tr', {}, [
    el('th', { texto: t('cotizador.codigo') }),
    el('th', { texto: t('cotizador.concepto') }),
    el('th', { texto: t('resultado.tramo') }),
    el('th', { clase: 'celda-monto', texto: t('cotizador.venta') }),
    veCosto ? el('th', { clase: 'celda-monto', texto: t('cotizador.costo') }) : null,
    veCosto ? el('th', { clase: 'celda-monto', texto: t('cotizador.margen') }) : null,
  ]);

  const filas = lineas.map((l) =>
    el('tr', { clase: l.direccion === 'INGRESO' ? 'fila-ingreso' : undefined }, [
      el('td', {}, [el('code', { texto: l.codigo_cargo })]),
      el('td', {}, [
        // `opcion_linea.descripcion` es el literal del proveedor congelado.
        literal(l.descripcion) ?? el('span', { clase: 'tenue', texto: '—' }),
        l.inclusion === 'ADICIONAL'
          ? null
          : // [Invariante 1][H62] Un INCLUIDO no se suma: se marca para poder
            // explicar por que el BAF esta en la lista y no en el total.
            el('span', {
              clase: 'marca-inclusion',
              texto: t(`inclusion.${l.inclusion}`),
            }),
        l.direccion === 'INGRESO'
          ? // [Invariante 2][H23] Ingreso nuestro. Entra al margen y no va en el
            // documento del cliente.
            el('span', { clase: 'marca-ingreso', texto: t('direccion.INGRESO') })
          : null,
        l.es_estimado
          ? el('span', { clase: 'marca-estimado', texto: t('cotizador.esEstimado') })
          : null,
      ]),
      el('td', { texto: t(`tramo.${l.tramo}`) }),
      el('td', { clase: 'celda-monto', texto: formatearMoneda(l.venta, moneda, idioma) }),
      veCosto
        ? el('td', {
            clase: 'celda-monto',
            texto: formatearMoneda(l.costo, moneda, idioma),
          })
        : null,
      veCosto
        ? el('td', {
            clase: 'celda-monto',
            texto: formatearMoneda(l.margen, moneda, idioma),
          })
        : null,
    ]),
  );

  return el('table', { clase: 'tabla-cargos tabla-lineas' }, [
    el('thead', {}, [cabecera]),
    el('tbody', {}, filas),
  ]);
}

function pintarNoValorados(
  titulo: string,
  cargos: readonly CargoNoValorado[],
  t: Traductor,
  clase: string,
): HTMLElement | null {
  if (cargos.length === 0) return null;

  return el('div', { clase: `bloque-no-valorados ${clase}` }, [
    el('h3', { texto: titulo }),
    el(
      'ul',
      {},
      cargos.map((c) =>
        el('li', {}, [
          el('code', { texto: c.codigo_cargo }),
          el('span', { clase: 'tenue', texto: t(`tramo.${c.tramo}`) }),
          // El literal, que es lo que el agente tiene que leer para decidir.
          literal(c.descripcion),
          el('span', { clase: 'motivo', texto: t(`motivoFuera.${c.motivo}`) }),
          ...(c.condiciones ?? []).map((k) =>
            el('div', {
              clase: 'condicion',
              texto: [
                k.variable,
                k.umbral_desde === null ? null : `≥ ${k.umbral_desde}`,
                k.umbral_hasta === null ? null : `≤ ${k.umbral_hasta}`,
                k.unidad,
                k.valor_texto,
              ]
                .filter((x) => x !== null && x !== '')
                .join(' '),
            }),
          ),
        ]),
      ),
    ),
  ]);
}
